import type { Candidate } from 'model/Candidate/Candidate';
import { ExtraConfig, IndeedConfig } from 'shared/legacy/hooks/useBrowserExtension/indeed/indeed.types';

export enum SyncStatus {
  Pending = 'pending',
  Synced = 'synced',
  Error = 'error',
}

export enum IndeedConversationScopeKey {
  JOB_SEEKER_KEY = 'JOB_SEEKER_KEY',
  ADVERTISER_KEY = 'ADVERTISER_KEY',
  ADVERTISER_USER_KEY = 'ADVERTISER_USER_KEY',
}

export enum IndeedConversationAuthorRole {
  EMPLOYER = 'EMPLOYER',
  JOB_SEEKER = 'JOB_SEEKER',
}

export type UsePullIndeedMessageBasedOnCandidateOptions = {
  candidate?: Candidate | null;
  indeedConfig: IndeedEnhancedConfig;
};

export interface PullIndeedMessagesResult {
  syncedStatus: SyncStatus;
  syncedAt: Date | null;
  hasMessages: boolean;
}

export type PullIndeedMessageReturnType = () => Promise<PullIndeedMessagesResult>;

export type IndeedEnhancedConfig = IndeedConfig & ExtraConfig;

export type getContactSummaryInfosProps = {
  indeedConfig: IndeedEnhancedConfig;
  candidateId: string;
};

export type getJobSeekerKeyOnIndeedBasedOnCandidateNameProps = {
  tenantId: number;
  candidateId: string;
  candidateName: string;
  indeedConfig: IndeedEnhancedConfig;
};

export type getJobSeerByMongoOrIndeedProps = {
  candidateId: string;
  tenantId: number;
  candidateName: string;
  indeedConfig: IndeedEnhancedConfig;
  userEmail?: string;
};

export type IndeedCandidateConversation = {
  id: string;
  __typename: string;
  participants: IndeedCandidateConversationParticipants[];
  scope: IndeedCandidateConversationScope[];
};

export type IndeedCandidateConversationScope = {
  key: 'JOB_SEEKER_KEY' | 'ADVERTISER_KEY' | 'ADVERTISER_USER_KEY';
  value: string;
  __typename: string;
};

export type IndeedCandidateConversationParticipants = {
  accountKey: string;
  externalParticipantId: string;
  participantName: string;
  role: string;
};

export type IndeedCandidateConversationResponse = {
  data: {
    findConversations: {
      conversations: IndeedCandidateConversation[];
    };
  };
};

export type IndeedCandidateMessagesPageInfo = {
  hasPreviousPage: boolean;
  startCursor: string;
  hasNextPage: boolean;
  endCursor: string;
};

export type getConversationsIdBasedOnJobSeekerProps = {
  indeedConfig: IndeedEnhancedConfig;
  jobSeekerKey: string;
  tenantId: number;
  candidateId: string;
};

export type buildMessagesBasedOnConversationsIdProps = {
  indeedConfig: IndeedEnhancedConfig;
  conversationsId: string[];
  licenseEmailAddress: string | null;
  tenantId: number;
  candidateId: string;
  candidateName: string;
  userEmail?: string;
};

export interface IndeedMessage {
  id: string;
  senderId: string;
  content: string;
  fromSelf: boolean;
  sentAt: number;
  conversationId: string;
  licenseEmailAddress: string | null;
  type: string;
  jobInfo?: TimelineModuleResultData;
}

export type TimelineModuleResultData = {
  jobTitle: string;
  company: string;
  jobDescription: string;
};

export type IndeedCandidateMessagesResponse = {
  data: {
    conversation: {
      eventsConnection: {
        pageInfo: IndeedCandidateMessagesPageInfo;
        edges: IndeedCandidateMessagesEdgeAttribute[];
      };
    };
  };
};

export type IndeedCandidateMessagesEdgeAttribute = {
  id: string;
  node: {
    id: string;
    author: {
      role: string;
      accountKey: string;
    };
    messageBody: string;
    publicationDateTime: string;
    type: string;
    timelineModuleResult?: {
      data: string;
    };
  };
};

export type FindLastConversationsByContextQueryType = {
  operationName: string;
  query: string;
  variables: {
    contexts: string[];
    last: number;
    before?: string;
  };
};

export type FindMessagesByConversationGQLQueryType = {
  operationName: string;
  query: string;
  variables: {
    conversationId: string;
    previousPageToken?: string;
  };
};
